import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useAuth } from '@clerk/clerk-react';
import { getGlobalApiClient } from '../utils/apiClient.js';

/**
 * IndexingStatusIndicator component
 * Shows real-time indexing status for knowledge base files
 */
const IndexingStatusIndicator = ({
  fileId,
  fileName,
  initialStatus,
  initialProgress = 0,
  showRetry = true,
  onStatusChange,
  compact = false
}) => {
  const { theme } = useTheme();
  const { getToken } = useAuth();
  const [status, setStatus] = useState(initialStatus);
  const [progress, setProgress] = useState(initialProgress);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Get API client instance
  const apiClient = getGlobalApiClient(getToken);

  // Auto-refresh status for active indexing
  useEffect(() => {
    if (status === 'indexing' || status === 'retrying') {
      const interval = setInterval(() => {
        fetchStatus();
      }, 5000); // Check every 5 seconds

      return () => clearInterval(interval);
    }
  }, [status]);

  const fetchStatus = async () => {
    try {
      const data = await apiClient.get(`/indexing/statistics?fileId=${fileId}`);
      if (data.success && data.data.currentFile) {
        setStatus(data.data.currentFile.status);
        setProgress(data.data.currentFile.progress || 0);
        setLastUpdated(new Date());
        onStatusChange?.(data.data.currentFile.status, data.data.currentFile.progress);
      }
    } catch (error) {
      console.error('Error fetching indexing status:', error);
    }
  };

  const handleRetry = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiClient.post(`/indexing/retry/${fileId}`);
      if (data.success) {
        setStatus('retrying');
        setProgress(0);
        onStatusChange?.('retrying', 0);
      } else {
        setError(data.error?.message || 'Retry failed');
      }
    } catch (error) {
      console.error('Error retrying indexing:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (currentStatus) => {
    switch (currentStatus) {
      case 'indexed':
        return {
          icon: '✅',
          color: theme === 'dark' ? 'text-green-400' : 'text-green-600',
          bg: theme === 'dark' ? 'bg-green-900/20 border-green-500/30' : 'bg-green-50 border-green-200',
          label: 'Indexed',
          description: 'File is searchable'
        };
      case 'indexing':
      case 'retrying':
        return {
          icon: '🔄',
          color: theme === 'dark' ? 'text-blue-400' : 'text-blue-600',
          bg: theme === 'dark' ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200',
          label: status === 'retrying' ? 'Retrying' : 'Indexing',
          description: 'Processing file for search'
        };
      case 'pending':
      case 'queued':
        return {
          icon: '⏳',
          color: theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600',
          bg: theme === 'dark' ? 'bg-yellow-900/20 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200',
          label: status === 'queued' ? 'Queued' : 'Pending',
          description: 'Waiting to be processed'
        };
      case 'failed':
        return {
          icon: '❌',
          color: theme === 'dark' ? 'text-red-400' : 'text-red-600',
          bg: theme === 'dark' ? 'bg-red-900/20 border-red-500/30' : 'bg-red-50 border-red-200',
          label: 'Failed',
          description: 'Indexing encountered an error'
        };
      default:
        return {
          icon: '❓',
          color: theme === 'dark' ? 'text-gray-400' : 'text-gray-600',
          bg: theme === 'dark' ? 'bg-gray-900/20 border-gray-500/30' : 'bg-gray-50 border-gray-200',
          label: 'Unknown',
          description: 'Status unknown'
        };
    }
  };

  const statusConfig = getStatusConfig(status);

  if (compact) {
    return (
      <div className={`flex items-center space-x-2 px-2 py-1 rounded-full border ${statusConfig.bg}`}>
        <span className="text-sm">{statusConfig.icon}</span>
        <span className={`text-xs font-medium ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
        {status === 'indexing' && progress > 0 && (
          <span className={`text-xs ${statusConfig.color}`}>
            {progress}%
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`border rounded-lg p-4 ${statusConfig.bg}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{statusConfig.icon}</span>
          <div>
            <h4 className={`font-medium ${statusConfig.color}`}>
              {statusConfig.label}
            </h4>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              {statusConfig.description}
            </p>
          </div>
        </div>
        {showRetry && status === 'failed' && (
          <button
            onClick={handleRetry}
            disabled={loading}
            className={`px-3 py-1 text-sm rounded ${
              loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? 'Retrying...' : 'Retry'}
          </button>
        )}
      </div>

      {/* Progress bar for indexing */}
      {(status === 'indexing' || status === 'retrying') && (
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className={statusConfig.color}>Progress</span>
            <span className={statusConfig.color}>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className={`mt-2 p-2 rounded ${theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50'}`}>
          <p className={`text-sm ${theme === 'dark' ? 'text-red-300' : 'text-red-600'}`}>
            {error}
          </p>
        </div>
      )}

      {/* File info */}
      {fileName && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            File: {fileName}
          </p>
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default IndexingStatusIndicator;